<template>
	<div class="flow-date-time-picker-container">
		<DatePicker
			v-model="innerValue"
			class="flow-date-time-picker"
			:mode="mode"
			:is-range="isRange"
			:popover="popover"
			@input="handleInput"
		>
			<template #default="{ inputValue, togglePopover }">
				<div class="flow-date-time-picker-icon" @click="togglePopover()">
					<Icon
						v-if="mode === 'date' || mode === 'dateTime'"
						name="i-calendar"
						size="small"
						type="filled"
						state="dark"
						:effects="false"
					/>
					<Icon
						v-if="mode === 'time'"
						name="i-clock-fill"
						size="small"
						type="filled"
						state="dark"
						:effects="false"
					/>
				</div>
				<input
					v-if="!isRange"
					:value="inputValue"
					:placeholder="placeholder"
					class="flow-date-time-picker-input"
					readonly
					@click="togglePopover()"
				/>
				<input
					v-if="isRange"
					:value="inputValue.start"
					:placeholder="placeholder"
					class="flow-date-time-picker-input range"
					readonly
					@click="togglePopover()"
				/>
				<Icon v-if="isRange" name="i-arrow-right" type="filled" size="small" :effects="false" />
				<input
					v-if="isRange"
					:value="inputValue.end"
					:placeholder="placeholder"
					class="flow-date-time-picker-input range"
					readonly
					@click="togglePopover()"
				/>
			</template>
		</DatePicker>
	</div>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";
//@ts-expect-error
import { DatePicker } from "v-calendar";
import Icon from "./../../Icon/Icon.vue";

export default defineComponent({
	name: "DateTimePicker",

	components: {
		DatePicker,
		Icon
	},
	props: {
		/**
		 * Use Date object or for range create object with 'start' and 'end' property
		 */
		value: {
			type: [Object, Date],
			default: () => null //new Date()
		},
		/**
		 * Place holder to display
		 */
		placeholder: {
			type: String,
			default: ""
		},
		/**
		 * 'date' | 'dateTime' | 'time'
		 */
		mode: {
			type: String,
			default: "date"
		},
		/**
		 * true | false <br/>
		 * Note : if this is true then pass value as an Object like '{start:&lt;date object&gt;,end:&lt;date object&gt;}'
		 */
		isRange: {
			type: Boolean,
			default: false
		},
		/**
		 * placement for popover of date-picker
		 */
		popover: {
			type: Object as PropType<DatePickerPopover>,
			default: () => undefined
		}
	},
	computed: {
		innerValue: {
			// getter
			get() {
				return this.value;
			},
			// setter
			set(newValue: Date) {
				/**
				 * Whenever value is updated.<br/>
				 * use 'value.sync' in parent component to achieve 2 way binding.
				 *
				 * @event update:value
				 */
				this.$emit("update:value", newValue);
			}
		}
	},
	methods: {
		handleInput(value: Date) {
			/**
			 * @event input
			 */
			this.$emit("input", value);
		}
	}
});

export type DatePickerPlacement =
	| "auto"
	| "top"
	| "right"
	| "bottom"
	| "left"
	| "top-start"
	| "bottom-start"
	| "top-end"
	| "bottom-end"
	| "right-start"
	| "right-end"
	| "left-start"
	| "left-end";

export type DatePickerPopover = {
	placement: DatePickerPlacement;
	positionFixed?: Boolean;
	hideDelay?: Number;
	keepVisibleOnInput?: Boolean;
	modifiers?: [];
	showDelay?: Number;
	transition?: "slide-fade" | "fade" | "none" | "";
	visibility?: "hover-focus" | "hover" | "focus" | "visible" | "hidden";
};
</script>
<style lang="scss">
div.flow-date-time-picker-container {
	display: flex;
	height: 36px;

	&[state="error"] {
		.flow-date-time-picker {
			box-shadow: 0 0 0 1px var(--dashboard-danger) !important;
		}
	}

	.flow-date-time-picker-icon {
		height: 36px;
		width: 36px;
		display: flex;
		justify-content: center;
		align-items: center;
		cursor: pointer;
		border-right: 1px solid var(--dashboard-border);
		// background-color: var(--dashboard-element-light);
	}
	.flow-date-time-picker {
		display: flex;
		align-items: center;
		border-radius: 4px;
		overflow: hidden;
		background-color: var(--dashboard-input);
		&:focus-within {
			box-shadow: 0 0 0 1px var(--dashboard-primary);
		}
		.vc-container {
			--text-sm: 12px;
			--text-lg: 16px;
			--blue-200: var(--primary-100);
			--blue-600: var(--primary-300);
			color: var(--dashboard-font);
			background-color: var(--dashboard-element-light);
			border-color: var(--dashboard-border-light);
			border-radius: 4px;

			.vc-title {
				color: var(--dashboard-font);
				&:hover {
					color: var(--dashboard-font-dark);
					opacity: 1;
				}
			}
			.vc-nav-title:hover {
				background-color: var(--dashboard-element-light);
			}

			.vc-day-content {
				&:hover {
					color: var(--dashboard-font-dark) !important;
					background-color: var(--dashboard-element);
				}
			}

			.vc-date {
				.vc-weekday,
				.vc-year,
				.vc-month,
				.vc-day {
					color: var(--dashboard-font);
				}
			}

			.vc-select select {
				background-color: var(--dashboard-input);
				border-width: 1px;
				color: var(--dashboard-font);
				border-color: var(--dashboard-border-light);
			}
			.vc-am-pm {
				background: var(--dashboard-input);
				button {
					color: var(--dashboard-font);
				}
			}
			.vc-time-icon {
				display: none;
			}
			.vc-date-time {
				margin-left: 0px !important;
			}
		}

		.vc-time-picker {
			align-items: flex-start;
			&.vc-bordered {
				border-top: 1px solid var(--dashboard-border-light);
			}
		}
	}

	.flow-date-time-picker-input {
		height: 36px;
		background-color: inherit;
		outline: none;
		appearance: unset !important;
		background-image: none !important;
		border: none;
		color: var(--dashboard-font-dark);
		font-size: 12px;
		font-weight: normal;
		line-height: 14px;
		box-sizing: border-box;
		font-family: inherit;
		padding: 11px 12px 11px 12px;

		&::placeholder {
			color: var(--dashboard-input-placeholder);
		}
		&.range {
			max-width: 160px;
		}
	}

	.vc-nav-popover-container {
		background-color: var(--dashboard-element);
		color: var(--dashboard-font-dark);
	}
	.vc-nav-item {
		&:hover {
			color: var(--dashboard-font-dark);
			background-color: var(--dashboard-element-light);
		}
		&.is-active {
			color: var(--dashbaord-font-dark);
			background: var(--dashboard-primary);
		}
		&.is-current {
			border-color: var(--dashboard-font-light);
			border-width: 1px;
		}
	}
	.vc-arrow:hover {
		background: var(--dashboard-element);
		color: var(--dashboard-font-dark);
	}
	.vc-nav-arrow:hover {
		background: var(--dashboard-element-light);
	}

	.vc-date {
		display: none;
	}
}
</style>
